import { Component, ElementRef, Input, NgZone, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { catchError, map, of, take } from 'rxjs';
import { AutoComplete } from 'primeng/autocomplete';

import { SearchHistoryService } from '../../services/search-history.service';
import { SearchHistoryItem, StartResponsePayload } from '@cpak/adp-cpak-bff-service-models';
import { StartService } from '@cpak/main-features-lib/services';
import { SearchFacade } from '@cpak/main-features-lib/data-store';
import { AutocompleteGroupSuggestions } from '@cpak/interfaces';
import { TranslocoService } from '@ngneat/transloco';
import { ButtonNamesGA, confirmationDialogConfig } from '@cpak/consts';
import { ConfirmationService } from 'primeng/api';
import { ALLCOUNTRY } from '../navigation-bar/countries-const';

@Component({
  selector: 'cpak-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent {
  @Input() isPresentationMode = false;
  @ViewChild(AutoComplete) searchAutocomplete!: AutoComplete;
  @ViewChild('searchContainer') searchContainer!: ElementRef;

  public isShowTapToSearch = false;
  public searchInputValue = '';
  private userIds = '';
  private currentSearchResult = {
    label: ' ',
    value: 'current',
    items: [this.searchInputValue],
    class: 'current-group-item',
  };
  public suggestionResults: AutocompleteGroupSuggestions = [this.currentSearchResult];

  public readonly ButtonNamesGA = ButtonNamesGA;

  constructor(
    private router: Router,
    private activateRoute: ActivatedRoute,
    private searchHistoryService: SearchHistoryService,
    private startService: StartService,
    private ngZone: NgZone,
    private searchFacade: SearchFacade,
    private translocoService: TranslocoService,
    private confirmationService: ConfirmationService
  ) {
    this.activateRoute.queryParams.subscribe((params: Params) => {
      this.searchInputValue = params['searchQuery'];
    });

    const updatedSearchHistory = this.searchHistoryService.getUpdatedSearchHistory();

    if (!updatedSearchHistory.length) {
      this.startService
        .getStartResponse()
        .pipe(
          map((response: StartResponsePayload | null) => {
            if (response) {
              const searchHistoryQuery = response.data.searchHistory.map((item: SearchHistoryItem) => item.query);
              this.userIds = response.data.userInfo.user.id;
              this.translocoService.selectTranslate('recentSearches').subscribe(label => {
                const recentResults = {
                  label,
                  value: 'rs',
                  items: searchHistoryQuery,
                  class: 'recent-group-item',
                };
                this.searchHistoryService.updateFullSearchHistory([recentResults]);
              });
            }
          }),
          catchError(() => of([]))
        )
        .subscribe();
    }
  }

  public onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.selectItem();
    }
  }

  public showDropDown(): void {
    const updatedSearchHistory = this.searchHistoryService.getUpdatedSearchHistory();
    if (!this.searchAutocomplete.overlayVisible && (this.searchInputValue || updatedSearchHistory[0].items.length)) {
      this.updateSuggestions();
      this.searchAutocomplete.show();
    }
  }

  public confirmAction(): void {
    this.confirmationService.confirm({
      message: this.translocoService.translate('selectCountryBeforeSearch'),
      acceptLabel: 'OK',
      rejectVisible: false,

      ...confirmationDialogConfig,
    });
  }

  public updateSuggestions(): void {
    const updatedSearchHistory = this.searchHistoryService.getUpdatedSearchHistory();
    const historyItemLength = updatedSearchHistory[0]?.items.length;
    const inputValue = this.searchInputValue?.trim();
    this.currentSearchResult.items = [inputValue];

    this.isShowTapToSearch = !!inputValue;

    this.suggestionResults =
      historyItemLength && !!inputValue
        ? [this.currentSearchResult, ...updatedSearchHistory]
        : historyItemLength && !inputValue
        ? [...updatedSearchHistory]
        : [this.currentSearchResult];
  }

  public selectItem(): void {
    const currentCountryCode = localStorage.getItem(this.userIds + '-selected-country-territories');
    const userCountryCodeParse = JSON.parse(currentCountryCode!) as { country: string };
    if (userCountryCodeParse && userCountryCodeParse.country === ALLCOUNTRY) {
      this.confirmAction();
    } else {
      this.searchInputValue = this.searchInputValue?.trim();
      if (this.searchInputValue) {
        this.searchHistoryService.addLastSearchValue(this.searchInputValue);
        this.router.navigate(['/search'], {
          queryParams: { searchQuery: this.searchInputValue },
          queryParamsHandling: 'merge',
          state: { isSearchQuerySet: true },
        });
        this.searchFacade.resetSortingState();
      }

      // loosing focus after item selected in order to hide keyboard on tablets
      this.ngZone.onMicrotaskEmpty.pipe(take(1)).subscribe(() => {
        const searchInput = this.searchContainer.nativeElement.querySelector('.p-inputtext') as HTMLElement;
        searchInput.blur();
      });

      this.searchAutocomplete.hide();
    }
  }

  public resetCurrentSearchResult(): void {
    this.currentSearchResult.items = [];
    this.searchInputValue = '';
  }
}
